/**
 * The single client-side route tree, in data-router (library) mode.
 *
 * Auth itself lives outside this tree: `EntraSessionGate` renders the
 * initializing/error/unauthenticated/dev-identity-picker screens as plain
 * conditional renders, and only mounts `<RouterProvider>` once a session is
 * authenticated — see `EntraShell.tsx`. The multi-origin MSAL sign-in bounce
 * (`entra-auth-context.tsx`) already carries the full destination path+search
 * across its redirect loop, so a guarded deep link still lands on the exact
 * original URL once the router mounts post-auth, with no router-side
 * reimplementation of that flow needed.
 *
 * @module router/routes
 */

import React from "react";

import { createBrowserRouter, Navigate, Outlet, redirect, type RouteObject } from "react-router";

import { AuthenticatedWorkspace } from "@/components/shell/AuthenticatedWorkspace";
import { type CrumbContext, programmeCrumb } from "@/components/shell/RouteBreadcrumb";
import { ADMIN_AREA } from "@/router/admin/admin-area";

import { AccessDeniedRoute } from "./AccessDeniedRoute";
import { AdminIndexRoute, AdminRoute, useIsPlatformAdmin } from "./admin/AdminRoute";
import {
  AdminAccessRoute,
  AdminAuditRoute,
  AdminDisciplinesRoute,
  AdminProgrammesRoute,
  AdminProviderRoute,
  AdminTeamsRoute,
} from "./admin/AdminSectionRoutes";
import { PlatformCreateTenantRoute, PlatformManageTenantsRoute } from "./admin/PlatformAdminRoutes";
import { ModuleWorkspaceRedirect } from "./module-workspace/ModuleWorkspaceRedirect";
import { NotFoundPage, NotFoundRoute } from "./NotFoundRoute";
import { OverviewRoute } from "./OverviewRoute";
import { PortfolioRoute } from "./portfolio/PortfolioRoute";
import { RevisionComparisonRoute } from "./portfolio/RevisionComparisonRoute";
import { ProgrammeLayout } from "./programme-editor/ProgrammeLayout";
import { editorRoutes } from "./programme-editor/surface-routes";
import { CreateModuleRoute } from "./programmes/CreateModuleRoute";
import { CreateProgrammeRoute } from "./programmes/CreateProgrammeRoute";
import { ProgrammeListActions } from "./programmes/ProgrammeActions";
import { ProgrammesRoute } from "./programmes/ProgrammesRoute";
import { ProgrammeTeamsRoute } from "./programmes/ProgrammeTeamsRoutes";
import { ProgrammeViewerRoute } from "./reports/ProgrammeViewerRoute";
import { ReportsLayout } from "./reports/ReportsLayout";
import { RequirePermission } from "./RequirePermission";
import { RootErrorBoundary, RootLayout } from "./RootLayout";
import { SettingsRoute } from "./settings/SettingsRoute";
import { useWorkspaceOutletContext } from "./workspace-context";

function RequireTenantAdmin({ children }: { children: React.ReactNode }) {
  const { canManageTenant } = useWorkspaceOutletContext();
  return <RequirePermission ok={canManageTenant}>{children}</RequirePermission>;
}

/**
 * Tenant administration, which a platform admin has no tenant for.
 *
 * `/admin` picks its console by role, so a platform admin reaching one of the
 * tenant sections would get it wrapped in the platform tab strip, reading a
 * tenant their account is not in.
 */
function TenantAdminOnly(): React.ReactElement {
  return useIsPlatformAdmin() ? <Navigate to="/admin/manage" replace /> : <Outlet />;
}

function RequirePlatformAdmin({ children }: { children: React.ReactNode }) {
  return <RequirePermission ok={useIsPlatformAdmin()}>{children}</RequirePermission>;
}
function RequireReportsAccess({ children }: { children: React.ReactNode }) {
  const { canViewReports } = useWorkspaceOutletContext();
  return <RequirePermission ok={canViewReports}>{children}</RequirePermission>;
}

function RequireProgrammeTeamAccess({ children }: { children: React.ReactNode }) {
  const { canManageProgrammeTeam } = useWorkspaceOutletContext();
  return <RequirePermission ok={canManageProgrammeTeam}>{children}</RequirePermission>;
}

function RequireWorkspaceAccess({ children }: { children: React.ReactNode }) {
  const { canAccessWorkspace } = useWorkspaceOutletContext();
  return <RequirePermission ok={canAccessWorkspace}>{children}</RequirePermission>;
}

function lazyNamed<K extends string>(
  loader: () => Promise<Record<K, React.ComponentType>>,
  name: K,
): RouteObject["lazy"] {
  return async () => ({ Component: (await loader())[name] });
}

const reportRoutes: RouteObject[] = [
  { index: true, loader: () => redirect("library") },
  {
    path: "library",
    handle: { crumb: "Programme library" },
    lazy: lazyNamed(() => import("@/components/reports/ProgrammeLibrary"), "ProgrammeLibrary"),
  },
  {
    path: "dashboard",
    handle: { crumb: "Dashboard" },
    lazy: lazyNamed(() => import("@/components/reports/Dashboard"), "Dashboard"),
  },
  {
    path: "disciplines",
    handle: { crumb: "Disciplines" },
    lazy: lazyNamed(() => import("@/components/reports/SchoolReport"), "SchoolReport"),
  },
  {
    path: "compare",
    handle: { crumb: "Compare" },
    lazy: lazyNamed(() => import("@/components/reports/ProgrammeCompare"), "ProgrammeCompare"),
  },
  {
    path: "analysis",
    handle: { crumb: "Analysis" },
    lazy: lazyNamed(() => import("@/components/reports/Analysis"), "Analysis"),
  },
  {
    path: "validation",
    handle: { crumb: "Validation" },
    lazy: lazyNamed(() => import("@/components/reports/Validation"), "Validation"),
  },
  {
    path: "modules",
    handle: { crumb: "Module catalogue" },
    lazy: lazyNamed(() => import("@/components/reports/ModuleCatalogue"), "ModuleCatalogue"),
  },
  {
    path: "programmes/:programmeId",
    handle: { crumb: "Programme" },
    Component: ProgrammeViewerRoute,
  },
];

/** A catch-all for a known area, drawn inside that area's own layout. */
const notFound: RouteObject = {
  path: "*",
  handle: { crumb: "Page not found" },
  Component: NotFoundPage,
};

export function createAppRoutes(): RouteObject[] {
  return [
    {
      Component: RootLayout,
      ErrorBoundary: RootErrorBoundary,
      children: [
        {
          path: "/",
          handle: { crumb: "Overview" },
          Component: AuthenticatedWorkspace,
          children: [
            {
              index: true,
              // On the index, not on `/`: every screen sits under that one,
              // and inheriting it would offer this page where none fits.
              handle: { guide: "at-a-glance" },
              // Legacy `?programme=<id>` deep links (E2E fixtures, external
              // links) become a real redirect instead of the one-shot
              // useEffect this used to be.
              loader: ({ request }) => {
                const query = new URL(request.url).searchParams;
                const programmeId = query.get("programme");
                if (!programmeId) {
                  return null;
                }
                // The module notifications name one, and dropping it opened
                // the programme rather than the module the message was about.
                const moduleId = query.get("module");
                const base = `/programmes/${encodeURIComponent(programmeId)}`;
                const path = moduleId
                  ? `${base}/modules/${encodeURIComponent(moduleId)}`
                  : `${base}/overview/identity`;
                // Everything else the link carried travels with it. `?dev=true`
                // is read off the address the editor renders at, so dropping it
                // here put the dev toggle out of reach of its own entry point.
                query.delete("programme");
                query.delete("module");
                const rest = query.toString();
                return redirect(rest ? `${path}?${rest}` : path);
              },
              Component: OverviewRoute,
            },
            {
              // No guard: every signed-in person has settings of their own.
              path: "settings",
              handle: { crumb: "Settings" },
              Component: SettingsRoute,
            },
            {
              path: "programmes",
              handle: {
                crumb: "Programmes",
                action: ProgrammeListActions,
                guide: "programme-lifecycle",
              },
              element: (
                <RequireWorkspaceAccess>
                  <ProgrammesRoute />
                </RequireWorkspaceAccess>
              ),
            },
            {
              path: "portfolio",
              handle: { crumb: "Portfolio", guide: "programme-portfolio" },
              element: (
                <RequireWorkspaceAccess>
                  <PortfolioRoute />
                </RequireWorkspaceAccess>
              ),
            },
            {
              path: "portfolio/programmes/:programmeId/compare",
              handle: {
                guide: "programme-portfolio",
                crumb: [{ label: "Portfolio", to: "/portfolio" }, { label: "Revision comparison" }],
              },
              element: (
                <RequireWorkspaceAccess>
                  <RevisionComparisonRoute />
                </RequireWorkspaceAccess>
              ),
            },
            // An unknown address under a workspace area keeps the shell.
            { ...notFound, path: "portfolio/*" },
            { ...notFound, path: "programme-teams/*" },
            {
              path: "programmes/new",
              handle: {
                guide: "creating-a-programme",
                crumb: [{ label: "Programmes", to: "/programmes" }, { label: "New programme" }],
              },
              element: (
                <RequireWorkspaceAccess>
                  <CreateProgrammeRoute />
                </RequireWorkspaceAccess>
              ),
            },
            {
              path: "modules/new",
              handle: {
                guide: "building-an-assigned-module",
                crumb: [{ label: "Programmes", to: "/programmes" }, { label: "New module" }],
              },
              element: (
                <RequireWorkspaceAccess>
                  <CreateModuleRoute />
                </RequireWorkspaceAccess>
              ),
            },
            {
              path: "programme-teams",
              handle: { crumb: "Programme teams", guide: "programme-teams" },
              element: (
                <RequireProgrammeTeamAccess>
                  <ProgrammeTeamsRoute />
                </RequireProgrammeTeamAccess>
              ),
            },
            {
              // The chosen programme, so a team is addressable and the narrow
              // layout has somewhere to go back to. Same element: the route
              // parameter is the selection.
              path: "programme-teams/:programmeId",
              handle: { crumb: "Programme teams", guide: "programme-teams" },
              element: (
                <RequireProgrammeTeamAccess>
                  <ProgrammeTeamsRoute />
                </RequireProgrammeTeamAccess>
              ),
            },
            {
              path: "admin",
              handle: {
                crumb: (_params: unknown, { isPlatformAdmin }: CrumbContext) =>
                  isPlatformAdmin ? ADMIN_AREA.platform : ADMIN_AREA.client,
              },
              element: (
                <RequireTenantAdmin>
                  <AdminRoute />
                </RequireTenantAdmin>
              ),
              children: [
                {
                  index: true,
                  handle: { guide: "client-administration" },
                  element: <AdminIndexRoute />,
                },
                {
                  element: <TenantAdminOnly />,
                  children: [
                    {
                      path: "programmes",
                      handle: { crumb: "Programmes", guide: "client-admin-programmes-and-teams" },
                      element: <AdminProgrammesRoute />,
                    },
                    {
                      path: "provider",
                      handle: {
                        crumb: "Provider settings",
                        guide: "client-admin-provider-settings",
                      },
                      element: <AdminProviderRoute />,
                    },
                    {
                      path: "access",
                      handle: { crumb: "Access", guide: "client-admin-access-and-permissions" },
                      element: <AdminAccessRoute />,
                    },
                    {
                      path: "disciplines",
                      handle: { crumb: "Disciplines", guide: "client-admin-disciplines" },
                      element: <AdminDisciplinesRoute />,
                    },
                    {
                      path: "teams",
                      handle: {
                        crumb: "Programme teams",
                        guide: "client-admin-programmes-and-teams",
                      },
                      element: <AdminTeamsRoute />,
                    },
                    {
                      path: "teams/:programmeId",
                      handle: {
                        crumb: "Programme teams",
                        guide: "client-admin-programmes-and-teams",
                      },
                      element: <AdminTeamsRoute />,
                    },
                    {
                      path: "audit",
                      handle: { crumb: "Audit trail", guide: "client-admin-governance-and-audit" },
                      element: <AdminAuditRoute />,
                    },
                  ],
                },
                // The platform console's own sections. They sit under the
                // same address because it is the other screen at `/admin`,
                // and the layout above already chose which console renders.
                {
                  path: "create",
                  handle: { crumb: "Create tenant" },
                  element: (
                    <RequirePlatformAdmin>
                      <PlatformCreateTenantRoute />
                    </RequirePlatformAdmin>
                  ),
                },
                {
                  path: "manage",
                  handle: { crumb: "Manage tenants" },
                  element: (
                    <RequirePlatformAdmin>
                      <PlatformManageTenantsRoute />
                    </RequirePlatformAdmin>
                  ),
                },
                {
                  path: "manage/:tenantId",
                  handle: {
                    crumb: [{ label: "Tenants", to: "/admin/manage" }, { label: "Tenant" }],
                  },
                  element: (
                    <RequirePlatformAdmin>
                      <PlatformManageTenantsRoute />
                    </RequirePlatformAdmin>
                  ),
                },
                notFound,
              ],
            },
            {
              path: "programmes/:programmeId",
              handle: {
                crumb: programmeCrumb(),
                guide: "developing-a-programme",
                // The shell swaps its sidebar for the surfaces on this.
                programmeShell: true,
              },
              element: (
                <RequireWorkspaceAccess>
                  <ProgrammeLayout />
                </RequireWorkspaceAccess>
              ),
              children: [
                ...editorRoutes(),
                {
                  // The module workspace became a surface of the editor. The
                  // old address is in notification rows already sent, so it
                  // forwards, inside the layout so unsaved work stays mounted.
                  path: "modules/:moduleId",
                  handle: { redirect: true },
                  element: <ModuleWorkspaceRedirect />,
                },
                // An address inside a known programme keeps its chrome. The
                // root `*` below stays outside the shell on purpose.
                notFound,
              ],
            },
            {
              path: "programmes/:programmeId/changes/:changeCaseId",
              handle: {
                guide: "programme-lifecycle",
                crumb: programmeCrumb("Change case"),
                programmeShell: true,
              },
              element: (
                <RequireWorkspaceAccess>
                  <ProgrammeLayout />
                </RequireWorkspaceAccess>
              ),
              children: [...editorRoutes(), notFound],
            },
            {
              path: "reports",
              handle: { crumb: "Reports", guide: "client-admin-reports" },
              element: (
                <RequireReportsAccess>
                  <ReportsLayout />
                </RequireReportsAccess>
              ),
              children: [...reportRoutes, notFound],
            },
            // Nested under AuthenticatedWorkspace, not a root-level sibling:
            // the header/nav must stay mounted when a RequirePermission
            // guard redirects here (e.g. an impersonated identity losing
            // access to the page it was just viewing) — a root-level
            // "access-denied" route would unmount the whole shell instead
            // of just swapping the content area.
            {
              path: "access-denied",
              handle: { crumb: "Access denied" },
              Component: AccessDeniedRoute,
            },
          ],
        },
        { path: "*", Component: NotFoundRoute },
      ],
    },
  ];
}

export function createAppRouter(basename: string) {
  return createBrowserRouter(createAppRoutes(), { basename });
}
